import {
  Box,
  BoxProps,
  Button,
  ButtonProps,
  Flex,
  FlexProps,
  Heading,
  IconButton,
  IconButtonProps,
  Skeleton,
  Text,
  TextProps,
} from "@chakra-ui/react";

import { format } from "date-fns";
import { IoClose } from "react-icons/io5";
import { seatColorEnum } from "../../constant";
import SeatLayoutLoader from "../SeatLayout/SeatLayoutLoader";

export interface SeatLayoutWrapperProps {
  seatLayoutRenderer: React.ReactNode;
  showCloseButton?: boolean;
  onClose?: () => void;
  seatAvailabilityConfig?: { label: string; color: string }[];
  movieShowDetail: {
    title: string;
    date: string;
    time: string;
    theater: string;
    release_date: string;
    genre: string;
    grade: string;
    duration: number;
  };
  screenDetail: {
    title: string;
    screenPosition: number;
    cinemaHall?: string;
    screenName?: string;
    showIdx: string;
    audiIdx: string;
    audi_layout: "row" | "col";
    cinemaHallIdx: string;
    seat_selected_time: string | null;
    location: string;
  };
  totalAmount: number;
  seatLoading?: boolean;
  actionButton?: React.ReactNode | string;
  actionButtonProps?: ButtonProps;
  showLayoutHeaderProps?: FlexProps;
  showLayoutSubtitleProps?: FlexProps;
  seatWrapperFooterFlexProps?: FlexProps;
  seatLabelFlexProps?: FlexProps;
  socketTimeTextProps?: TextProps;
  showChangeShowButton?: boolean;
  changeShowButtonProps?: ButtonProps;
  onChangeShow?: () => void;
  headerExtraActions?: React.ReactNode;
  time?: number;
  isContainer?: boolean;
  selectedSeats?: string[];
  showCloseButtonProps?: IconButtonProps;
  isSeatSelected?: boolean;
}

function SeatWrapperContainer({
  seatLayoutRenderer,
  screenDetail,
  movieShowDetail,
  showCloseButton = true,
  onClose,
  seatAvailabilityConfig,
  totalAmount,
  seatLoading = true,
  actionButton = "Proceed",
  actionButtonProps = {},
  seatWrapperFooterFlexProps = {},
  showLayoutHeaderProps = {},
  showLayoutSubtitleProps = {},
  seatLabelFlexProps = {},
  socketTimeTextProps = {},
  showChangeShowButton = false,
  changeShowButtonProps = {},
  onChangeShow,
  headerExtraActions,
  time,
  showCloseButtonProps,
  isSeatSelected,
  ...props
}: SeatLayoutWrapperProps & BoxProps & ButtonProps) {
  const formatTime = (t: number) => {
    const minutes = Math.floor(t / 60000);
    const seconds = Math.floor((t % 60000) / 1000);
    return `${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
  };

  return (
    <Flex
      direction="column"
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        maxH: "100dvh",
        maxW: "100vw",
        height: "100%",
        width: "100%",
        // Sit above the global mobile bottom nav (zIndex "sticky" = 1100) so the
        // immersive seat-selection takeover fully covers it and the Proceed
        // button in the footer below stays visible. Kept under modals (1400).
        zIndex: 1300,
      }}
      color="white"
      background="white"
      {...props}
    >
      <Flex
        alignItems="start"
        backgroundColor="primary.500"
        p={{ base: "0.5rem", md: "1rem" }}
        justifyContent="space-between"
        {...showLayoutHeaderProps}
      >
        {seatLoading ? (
          <Box>
            <Skeleton height="1.25rem" w="8rem" />
            <Skeleton height="0.5rem" mt="0.5rem" w="12rem" />
          </Box>
        ) : (
          <Box flex={1}>
            <Flex
              justifyContent={"space-between"}
              gap={2}
              alignItems={"flex-start"}
            >
              <Heading
                size={{
                  base: "xs",
                  md: "md",
                }}
                as="h1"
                noOfLines={{ base: 2, md: undefined }}
              >
                {movieShowDetail?.title}
              </Heading>
              <Flex
                gap={{
                  base: 1,
                  md: 2,
                }}
                alignItems={{
                  base: "flex-end",
                  md: "center",
                }}
                justifyContent={"flex-start"}
              >
                {showChangeShowButton && (
                  <Button
                    size={{ base: "xs", md: "sm" }}
                    colorScheme="primary"
                    variant="outline"
                    onClick={onChangeShow}
                    {...changeShowButtonProps}
                  >
                    Change Show
                  </Button>
                )}
                {headerExtraActions}
                {showCloseButton && (
                  <IconButton
                    colorScheme="primary"
                    rounded="full"
                    fontSize="1.5rem"
                    background="surface.400"
                    aria-label="Close"
                    size={{
                      base: "xs",
                      md: "sm",
                    }}
                    icon={<IoClose />}
                    onClick={onClose}
                    {...showCloseButtonProps}
                  />
                )}
              </Flex>
            </Flex>

            <Flex
              gap={5}
              color="whiteAlpha.800"
              fontSize={{
                base: "xs",
                md: "sm",
              }}
              fontWeight={500}
              {...showLayoutSubtitleProps}
            >
              {movieShowDetail && (
                <Text wordBreak={"break-word"}>
                  {movieShowDetail?.theater} {screenDetail?.title} -{" "}
                  {movieShowDetail?.date
                    ? format(movieShowDetail?.date, "MMM dd, EEE")
                    : ""}
                  , {movieShowDetail?.time}
                </Text>
              )}
            </Flex>
          </Box>
        )}
      </Flex>
      <Flex
        justifyContent={"space-between"}
        backgroundColor="info.700"
        {...seatLabelFlexProps}
      >
        <Flex
          p={{
            base: "0.5rem",
            md: "1rem",
          }}
          gap={{
            base: 2,
            md: 5,
          }}
          wrap="wrap"
        >
          {(seatAvailabilityConfig?.length
            ? seatAvailabilityConfig
            : (Object.keys(seatColorEnum) as Array<keyof typeof seatColorEnum>)
                .filter((key) => key !== "ROW LABEL" && key !== "HOLD")
                .map((key) => ({ label: key, color: seatColorEnum[key] }))
          ).map(({ label, color }) => (
            <Flex
              alignItems="center"
              key={label}
              gap={{
                base: "0.25rem",
                md: "1rem",
              }}
            >
              <Box
                borderRadius="0.25rem"
                w={{
                  base: "0.5rem",
                  md: "1rem",
                }}
                h={{
                  base: "0.5rem",
                  md: "1rem",
                }}
                background={color}
              />
              <Text
                fontSize={{
                  base: "xs",
                  md: "sm",
                }}
                textTransform="capitalize"
              >
                {label}
              </Text>
            </Flex>
          ))}
        </Flex>
        {time && (
          <Flex alignItems={"center"}>
            <Text
              fontSize={{
                base: "md",
                md: "3xl",
              }}
              pe="2"
              fontFamily="mono"
              fontWeight="bold"
              letterSpacing="wider"
              color={"surface.white"}
              textAlign="center"
              aria-live="polite"
              aria-label={`Elapsed time: ${formatTime(time)}-seat-layout-container`}
              {...socketTimeTextProps}
            >
              {formatTime(time)}
            </Text>
          </Flex>
        )}
      </Flex>
      <Flex
        alignItems="center"
        justifyContent="center"
        my={{
          base: "0.5rem",
          md: "1rem",
        }}
        flex={1}
        maxW="100vw"
        overflowY="auto"
      >
        {seatLoading ? <SeatLayoutLoader /> : seatLayoutRenderer}
      </Flex>

      {isSeatSelected && (
        <Flex
          p={{
            base: "0.5rem",
            md: "1rem",
          }}
          // Clear the home-indicator safe area on notched / gesture-nav devices.
          pb={{
            base: "calc(0.5rem + env(safe-area-inset-bottom))",
            md: "calc(1rem + env(safe-area-inset-bottom))",
          }}
          backgroundColor="info.700"
          borderTop="1px"
          borderColor="surface.200"
          alignItems="center"
          justifyContent="space-between"
          position="sticky"
          bottom={0}
          {...seatWrapperFooterFlexProps}
        >
          <Text fontSize={{ base: "md", md: "xl" }}>
            Total: Rs {totalAmount}
          </Text>
          {typeof actionButton === "string" ? (
            <Button
              rounded="full"
              colorScheme="primary"
              size={{ base: "xs", md: "md" }}
              {...actionButtonProps}
            >
              {actionButton}
            </Button>
          ) : (
            actionButton
          )}
        </Flex>
      )}
    </Flex>
  );
}

export default SeatWrapperContainer;
